import { useReducedMotion } from 'framer-motion';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import type { TooltipProps } from './tooltip.types.js';
import { TooltipElement } from './TooltipElement.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useModality } from '../../core/hooks/useModality.js';
import { usePoliteContainer } from '../hooks/usePoliteContainer.js';
import { useTooltipState } from '../hooks/useTooltipState.js';

const COMPONENT_NAME = 'Tooltip';

/**
 * Use the `Tooltip` component to display extra information when users hover on a trigger element.
 * @public
 */
export const Tooltip = /* @__PURE__ */ forwardRef<HTMLDivElement, TooltipProps>(
  (props, forwardedRef) => {
    const {
      children,
      text,
      placement = 'top',
      open,
      defaultOpen,
      onOpenChange,
      delay = 'default',
      disabled,
      id: propId,
      fallbackPlacements,
      style: consumerStyle,
      className: consumerClassName,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const {
      'data-dt-name': dataDtName = 'Trigger',
      ...remainingBehavioralTrackingProps
    } = behavioralTrackingProps;

    const generatedId = useId();
    const id = propId ?? generatedId;
    const shouldReduceMotion = useReducedMotion();

    const {
      isOpen,
      open: openTooltip,
      close: closeTooltip,
    } = useTooltipState({
      open,
      defaultOpen,
      onOpenChange,
      delay: shouldReduceMotion || delay === 'none' ? 0 : undefined,
    });

    const tooltipWrapperRef = useRef<HTMLSpanElement | null>(null);
    const [tooltipReferenceElement, setTooltipReferenceElement] = useState<
      Element | undefined
    >(undefined);

    /**
     * The tooltip/popper needs a reference element for positioning. As display contents on the span wrapper is necessary
     * to not interfere with flow content of any layout parent, this will invalidate any getBoundingClientRect() results on
     * the wrapping span. That is why we pass the first child element instead. Since the first child element could change
     * dynamically, we need to watch for changes in the DOM, to ensure the tooltip always has a reference element.
     */
    const mutationObserverRef = useRef(
      new MutationObserver((entries) => {
        for (const entry of entries) {
          const firstNode = entry.addedNodes.item(0);
          if (
            entry.type === 'childList' &&
            firstNode !== null &&
            firstNode instanceof Element &&
            firstNode !== tooltipReferenceElement
          ) {
            setTooltipReferenceElement(firstNode);
          }
        }
      }),
    );

    useEffect(
      () => {
        if (tooltipWrapperRef.current) {
          setTooltipReferenceElement(tooltipWrapperRef.current?.children?.[0]);
          mutationObserverRef.current.observe(tooltipWrapperRef.current, {
            childList: true,
            subtree: false, // only observe changes in the direct children of the wrapper, not deeper in the DOM
          });
        }

        return () => closeTooltip();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // If disabled switches to true at runtime and a tooltip is shown, it should
    // close immediately
    useEffect(() => {
      if (disabled) {
        closeTooltip();
      }
    }, [closeTooltip, disabled]);

    const isTooltipHovered = useRef(false);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => e.code === 'Escape' && closeTooltip(),
      [closeTooltip],
    );

    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    // Create a singleton container with live region attribute to read the contents
    // of the tooltip when it pops up
    const PoliteContainer = usePoliteContainer();

    const modality = useModality();

    return (
      <>
        <span
          style={{
            // Safari has currently an outstanding issue that results in content
            // being inaccessible, when rendered within a display: contents.
            display: 'contents',
          }}
          ref={tooltipWrapperRef}
          onMouseEnter={() => !disabled && openTooltip()}
          onMouseLeave={() =>
            // we need to wrap this in a requestAnimationFrame to be able to first set the isTooltipHovered state correctly in case the TooltipElement (including the gap) is hovered
            // if we don't, the tooltip is closed before the onMouseOver event of the TooltipElement is triggered (to set the right state), as the onMouseLeave of the tooltip trigger would be queued up afterwards
            requestAnimationFrame(() => {
              if (!disabled && !isTooltipHovered.current) {
                closeTooltip();
              }
            })
          }
          onFocus={() =>
            !disabled &&
            modality !== 'pointer' &&
            modality !== 'touch' &&
            openTooltip(true)
          }
          onBlur={
            () => !disabled && !isTooltipHovered.current && closeTooltip() // prevent closing when clicking on the gap
          }
          data-dt-name={dataDtName}
          {...remainingBehavioralTrackingProps}
          {...restProps}
          data-dt-component={dataDtComponent}
        >
          {children}
        </span>
        <PoliteContainer>
          {isOpen && (
            <TooltipElement
              ref={forwardedRef}
              triggerElement={tooltipReferenceElement}
              placement={placement}
              fallbackPlacements={fallbackPlacements}
              id={id}
              data-testid={dataTestId}
              data-dtrum-mask={dataDtrumMask}
              data-dtrum-allow={dataDtrumAllow}
              className={consumerClassName}
              style={consumerStyle}
              onMouseLeave={(event) =>
                !disabled &&
                // only close the tooltip if the mouse isn't over the trigger element
                event.relatedTarget !== tooltipReferenceElement &&
                closeTooltip()
              }
              onTooltipHovered={(isHovered: boolean) =>
                (isTooltipHovered.current = isHovered)
              }
              {...behavioralTrackingProps}
              data-dt-component={dataDtComponent}
            >
              {text}
            </TooltipElement>
          )}
        </PoliteContainer>
      </>
    );
  },
);
(Tooltip as typeof Tooltip & { displayName: string }).displayName =
  COMPONENT_NAME;
